import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {Faq, FaqApiService, FaqAuthorType, FaqsResponse, FaqStatus, ReferenceBookResponse, ReferenceBookType} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, getFormattedDateOrNull, RouterService, rxSpinner} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {combineLatest, defer, EMPTY, map, mergeMap, of, Subscription} from 'rxjs';
import {
    AsyncFieldViewModel,
    fieldExtractor,
    FilterAsyncField,
    FilterDictionaryField,
    FilterEvent,
    FilterField,
    FilterSelectField,
} from '../../../../components/full-table/filter/filter.component';
import {initialTableEvent, TableEvent} from '../../../../components/full-table/full-table.component';
import {INITIAL_PAGE_NUMBER, INITIAL_PAGE_SIZE} from '../../../../components/full-table/paging/paginator.component';
import {Column, ColumnType, CustomLayoutColumn, Row, StatusColumn} from '../../../../components/full-table/table/table.component';
import {mapFaqStatusToStyle} from '../../../../components/status/status.component';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {FAQ_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';

@Component({
    selector: 'af-faq-admin',
    standalone: false,
    templateUrl: './faq-admin.component.html',
    styleUrl: './faq-admin.component.scss',
})
export class FaqAdminComponent implements OnInit, OnDestroy {
    @Output() addQuestionClickListener = new EventEmitter<void>();
    @Output() hasDataListener = new EventEmitter<boolean>();

    answerAuthors: AsyncFieldViewModel[] = [];
    questionAuthors: AsyncFieldViewModel[] = [];
    filterFields: FilterField[] = [
        {name: 'query', type: 'text'},
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'category',
            dictionaryName: ReferenceBookType.FAQ_CATEGORY,
        },
        <FilterSelectField<FaqStatus>>{
            isMultiSelect: true,
            type: 'select',
            name: 'status',
            translationPrefix: 'faq',
            values: [FaqStatus.NEW, FaqStatus.PUBLISHED, FaqStatus.ANSWERED],
        },
        <FilterAsyncField>{
            isMultiSelect: true,
            type: 'select',
            name: 'questionAuthor',
            request: defer(() =>
                this.questionAuthors.length
                    ? of(this.questionAuthors)
                    : this.faqApiService.getFaqAuthors(FaqAuthorType.QUESTION).pipe(
                        map(names => {
                            this.questionAuthors = names.map(name => <AsyncFieldViewModel>{code: name, name_ru: name});
                            return this.questionAuthors;
                        })
                    )
            ),
        },
        <FilterAsyncField>{
            isMultiSelect: true,
            type: 'select',
            name: 'answerAuthor',
            request: defer(() =>
                this.answerAuthors.length
                    ? of(this.answerAuthors)
                    : this.faqApiService.getFaqAuthors(FaqAuthorType.ANSWER).pipe(
                        map(names => {
                            this.answerAuthors = names.map(name => <AsyncFieldViewModel>{code: name, name_ru: name});
                            return this.answerAuthors;
                        })
                    )
            ),
        },
        {name: 'dateRange', type: 'dateRange'},
    ];

    tableEvent: TableEvent = {
        ...initialTableEvent,
        spinnerId: 'faq',
        filter: {
            query: null,
        },
        sortBy: {
            columnName: 'updatedDate',
            direction: 'asc',
        },
    };
    dataSource: Row[] = [];
    totalElements: number;
    private loadSubscription: Subscription;

    constructor(
        private faqApiService: FaqApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private routerService: RouterService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
    ) {
    }

    ngOnInit(): void {
        this.loadFaqs();
    }

    ngOnDestroy(): void {
        this.loadSubscription?.unsubscribe();
    }

    loadFaqs(tableEvent?: TableEvent): void {
        if (tableEvent) {
            this.tableEvent = tableEvent;
        }
        this.loadSubscription?.unsubscribe();
        if (this.tableEvent?.spinnerId !== 'paging') {
            this.dataSource = [];
            this.totalElements = 0;
        }
        const filterEvent = tableEvent?.filter;
        this.loadSubscription = combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.FAQ_CATEGORY),
            this.faqApiService.getFaqs({
                hasPlainAnswer: true,
                hasFormattedAnswer: false,
                query: filterEvent?.query,
                dateFrom: getFormattedDateOrNull(filterEvent?.dateRange?.dateFrom, DATE_TIME_SERVER_FORMAT),
                dateTo: getFormattedDateOrNull(filterEvent?.dateRange?.dateTo, DATE_TIME_SERVER_FORMAT),
                statuses: fieldExtractor(filterEvent?.status),
                categories: fieldExtractor(filterEvent?.category),
                answeredBy: fieldExtractor(filterEvent?.answerAuthor),
                createdBy: fieldExtractor(filterEvent?.questionAuthor),
                limit: tableEvent?.page?.pageSize,
                offset: tableEvent?.page?.pageOffset,
            }),
        ])
            .pipe(rxSpinner(tableEvent?.spinnerId ?? 'initial'))
            .subscribe(([dictionary, response]) => this.buildDataSource(response, dictionary));
    }

    private buildDataSource(response: FaqsResponse, dictionary: ReferenceBookResponse[]): void {
        this.totalElements = response.total;
        const filterEvent = this.tableEvent?.filter;
        this.hasDataListener.emit(response.total > 0 || this.hasActiveFilter(filterEvent));
        this.dataSource = (response.items ?? []).map(item => <Row>{
            click: () => this.openFaqDetails(item.id),
            actions: <CustomLayoutColumn>{
                type: ColumnType.CUSTOM_LAYOUT,
                cellStyle: {
                    layoutDirection: 'row',
                    layoutAlignFirst: 'start',
                    layoutAlignSecond: 'center',
                    layoutGap: '8px',
                },
                layoutContentItems: [
                    {
                        type: 'icon',
                        text: 'ic_edit',
                        click: () => this.editFaq(item.id),
                        tooltip: 'i18n_af_labelEdit',
                        iconSize: 'i-16',
                    },
                    {
                        type: 'icon',
                        text: 'ic_delete',
                        click: () => this.deleteFaq(item),
                        tooltip: 'i18n_af_labelDelete',
                        iconSize: 'i-16',
                    },
                ],
            },
            status: <StatusColumn>{
                type: ColumnType.STATUS,
                text: 'i18n_af_faqStatus#' + item.status,
                style: mapFaqStatusToStyle(item.status),
            },
            category: <Column>{
                type: ColumnType.TEXT,
                text: findValueInDictionary(dictionary, item.category, 'name_ru'),
                cellStyle: {ellipsisText: true},
            },
            question: <Column>{
                type: ColumnType.TEXT,
                text: item.question,
                cellStyle: {ellipsisTextLarge: true},
            },
            answer: <Column>{
                type: ColumnType.TEXT,
                text: item.plainAnswer,
                cellStyle: {ellipsisTextLarge: true},
            },
            createdBy: <Column>{
                type: ColumnType.TEXT,
                text: item.createdBy,
                cellStyle: {ellipsisText: true},
            },
            answeredBy: <Column>{
                type: ColumnType.TEXT,
                text: item.answeredBy,
                cellStyle: {ellipsisText: true},
            },
            id: <Column>{
                type: ColumnType.TEXT,
                text: item.id,
            },
            createdDate: <Column>{
                type: ColumnType.DATETIME,
                text: item.createdDate,
            },
            updatedDate: <Column>{
                type: ColumnType.DATETIME,
                text: item.updatedDate,
            },
        });
    }

    private hasActiveFilter(filterEvent: FilterEvent): boolean {
        return !!filterEvent?.query?.trim()
            || !!fieldExtractor(filterEvent?.status)?.length
            || !!fieldExtractor(filterEvent?.category)?.length
            || !!fieldExtractor(filterEvent?.answerAuthor)?.length
            || !!fieldExtractor(filterEvent?.questionAuthor)?.length
            || !!(filterEvent?.dateRange?.dateFrom || filterEvent?.dateRange?.dateTo);
    }

    private openFaqDetails(id: string): void {
        this.routerService.navigate(Path.FAQ_DETAILS, {pathParams: {[FAQ_ID]: id}});
    }

    private editFaq(id: string): void {
        this.routerService.navigate(Path.FAQ_EDIT, {pathParams: {[FAQ_ID]: id}});
    }

    private deleteFaq(faq: Faq): void {
        this.dialogService.openDialog({
            title: 'i18n_af_faqDeleteDialogTitle',
            subtitle: 'i18n_af_faqDeleteDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDelete',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {id: faq.id},
        }).pipe(
            mergeMap(result => !result ? EMPTY : this.faqApiService.deleteFaq(faq.id).pipe(rxSpinner('deleting'))),
        ).subscribe(() => {
            this.snackbarService.showSnackBar('i18n_af_snackBarFaqDeleted');
            this.loadFaqs({
                page: {
                    pageSize: INITIAL_PAGE_SIZE,
                    pageNumber: INITIAL_PAGE_NUMBER,
                    pageOffset: INITIAL_PAGE_NUMBER * INITIAL_PAGE_SIZE,
                },
                spinnerId: 'initial',
            });
        });
    }
}
